import { useEffect, useState } from 'react';

import { AiFieldChrome } from '~/components/pages/ai/placements/preview/AiFieldChrome';
import { useAiTextActions } from '~/components/pages/ai/placements/preview/useAiTextActions';
import { TextField as KitTextField } from '~/components/form';
import type {
  PlacementUsageSnapshot,
  PlacementUserInstructionRequest,
} from '~/modules/query/ai/types';

/** What a preview's `onRunAi` hands back to the in-field AI menu. */
export interface PlacementAiAssistResponse {
  compiledPrompt?: string;
  outputText: string;
  reasoning?: string;
  usage?: PlacementUsageSnapshot;
}

export type PlacementRunAi = (
  request: PlacementUserInstructionRequest,
) => Promise<PlacementAiAssistResponse | null>;

/** The props every preview field takes from the registry. */
export interface PlacementPreviewFieldProps {
  aiDisableTooltip?: string;
  aiDisabled: boolean;
  /** Shows the insert-dynamic-field button. Defaults to true. */
  bracesButton?: boolean;
  /** Turns on the counter and the field's own max length. */
  characterLimit?: number;
  defaultValue?: string;
  disabledTooltip?: string;
  /** Replaces the AI-generated-content disclaimer. */
  disclaimerText?: string;
  /** Shows the insert-emoji button. Defaults to true. */
  emojiButton?: boolean;
  /** Draws the error message under the field and the error border. */
  errorText?: string;
  isDisabled: boolean;
  isRunning: boolean;
  /** The label above the field; an empty string hides the label row's text. */
  labelText?: string;
  /** A help icon beside the label, with this as its tooltip. */
  labelTooltip?: string;
  onRunAi?: PlacementRunAi;
  placeholder?: string;
  required?: boolean;
  /** Shows the disclaimer below the field. Defaults to true. */
  showDisclaimer?: boolean;
  /** Shown below the field, only while `characterLimit` is set. */
  tipText?: string;
}

/** The Text Field placement preview: the kit field inside the AI chrome (label row triggers, tip, disclaimer). */
export default function TextField({
  aiDisableTooltip,
  aiDisabled,
  bracesButton,
  characterLimit,
  defaultValue = '',
  disabledTooltip,
  disclaimerText,
  emojiButton,
  errorText,
  isDisabled,
  isRunning,
  labelText = 'Text field',
  labelTooltip,
  onRunAi,
  placeholder,
  required,
  showDisclaimer,
  tipText,
}: PlacementPreviewFieldProps) {
  const [text, setText] = useState(defaultValue);
  const ai = useAiTextActions({
    aiDisabled,
    isDisabled,
    isRunning,
    onRunAi,
    onTextChange: setText,
    text,
  });

  useEffect(() => {
    setText(defaultValue);
  }, [defaultValue]);

  const fieldId = 'placement-preview-text-field';

  return (
    <div title={isDisabled ? disabledTooltip : undefined}>
      <AiFieldChrome
        ai={ai}
        aiDisabled={aiDisabled}
        aiDisabledReason={aiDisableTooltip}
        bracesButton={bracesButton}
        characterLimit={characterLimit}
        disclaimerText={disclaimerText}
        emojiButton={emojiButton}
        fieldId={fieldId}
        hasAiHandler={Boolean(onRunAi)}
        isDisabled={isDisabled}
        labelText={labelText}
        labelTooltip={labelTooltip}
        onTextChange={setText}
        required={required}
        showDisclaimer={showDisclaimer}
        testId={`${fieldId}-ai`}
        text={text}
        tipText={tipText}
      >
        <KitTextField
          id={fieldId}
          label=""
          ariaLabel={labelText || 'Text field'}
          testId={`${fieldId}-input`}
          value={text}
          onChange={setText}
          placeholder={placeholder}
          disabled={isDisabled}
          required={required}
          error={errorText}
          helperText={isDisabled ? disabledTooltip : undefined}
          maxLength={characterLimit}
        />
      </AiFieldChrome>
    </div>
  );
}
